import * as React from 'react';
import { type IRiveProps } from '../Rive.props';

import { useEffect, useRef, useImperativeHandle, useState } from 'react';
import iframeSrc from '../assets/riveIframe.html?resource';
import type { IRiveImperativeActions } from '../Rive.types';
import { getThumbnailUrl, getFileAnimationUrl } from '../utils';

type messagePropsType = {
  src: any;
  autoplay: boolean;
  fit: string;
  alignment: string;
  artboard?: string;
  stateMachines?: string;
  isTouchScrollEnabled: boolean;
};

export const Rive: React.ForwardRefRenderFunction<
  IRiveImperativeActions,
  IRiveProps
> = (props, ref) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isPlayActive, setIsPlayActive] = useState(false);
  const [isRiveLoaded, setIsRiveLoaded] = useState(false);

  const playAnimation = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(
        {
          type: 'playAnimation',
        },
        '*',
      );
    }
  };
  const pauseAnimation = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(
        {
          type: 'pauseAnimation',
        },
        '*',
      );
    }
  };
  const createNewRive = () => {
    if (iframeRef.current) {
      const src = getFileAnimationUrl(props.metaSiteId, props.animationFileUrl);
      const messageProps: messagePropsType = {
        src,
        autoplay: !props.reducedMotion && props.isAutoPlayOn,
        isTouchScrollEnabled: props.isTouchScrollEnabled,
        fit: props.fit,
        alignment: props.alignment,
        artboard: props.artboard.length > 0 ? props.artboard : undefined,
        stateMachines:
          props.stateMachine.length > 0 ? props.stateMachine : undefined,
      };

      iframeRef?.current?.contentWindow?.postMessage(
        {
          type: 'initRiveInstance',
          props: messageProps,
          source: 'editor',
        },
        '*',
      );
      if (isPlayActive) {
        playAnimation();
      }
    }
  };

  useEffect(() => {
    if (iframeRef.current) {
      createNewRive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.fit,
    props.alignment,
    props.animationFileUrl,
    props.isAutoPlayOn,
    props.artboard,
    props.stateMachine,
    iframeRef.current,
  ]);

  useImperativeHandle(ref, () => ({
    play: () => {
      setIsPlayActive(true);
      playAnimation();
    },
    stop: () => {
      if (!props.isInPreviewMode) {
        setIsPlayActive(false);
        pauseAnimation();
      }
    },
  }));
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'riveComponentInstanceLoaded') {
        setIsRiveLoaded(true);
      }
    };

    window.addEventListener('message', handleMessage);
  }, []);

  return (
    <div id={props.id} style={{ overflow: 'hidden' }}>
      {!isRiveLoaded && (
        <img
          src={getThumbnailUrl(props.thumbnailFileUrl)}
          alt="Rive animation thumbnail"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
      <wix-iframe data-src={iframeSrc}>
        <iframe
          aria-hidden={props.isDecorative}
          title={props.alt}
          onLoad={createNewRive}
          ref={iframeRef}
          data-src={iframeSrc}
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </wix-iframe>
    </div>
  );
};
export default React.forwardRef(Rive);
